import { useAuth } from '@/lib/auth/auth';
import { APP_CONFIG } from '@/lib/app/config';
import { WebSearchCombobox } from '@/features/search/WebSearchCombobox';
import { isReadOnlyMarketAccess } from '@/features/market/marketAccess';
import { TAB_CONFIG } from '@/lib/navigation/navConfig';
import type { TabRouteName } from '@/lib/navigation/navConfig';
import { cn } from '@/lib/utils';
import { router, usePathname } from 'expo-router';
import { Icon } from '@/components/ui/icon';
import { Text } from '@/components/ui/text';
import { MapPinIcon, MenuIcon, PlusIcon } from 'lucide-react-native';
import * as React from 'react';
import { Linking, Pressable, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Svg, { Path } from 'react-native-svg';

const TOP_NAV_ROUTES: TabRouteName[] = ['events', 'map', 'activity'];

export function AppTopBar({ pulseBadgeLabel }: { pulseBadgeLabel: string | null }) {
  const session = useAuth((state) => state.session);
  const insets = useSafeAreaInsets();
  const pathname = usePathname();
  const cityName =
    session?.me.profile.home_city || session?.me.profile.browse_origin?.market_name || 'San Antonio';
  const activeTab =
    (Object.keys(TAB_CONFIG) as TabRouteName[]).find((name) => pathname.includes(name)) ?? 'events';
  const profileLabel = session ? TAB_CONFIG.profile.label : 'Sign in';
  const handleProfilePress = React.useCallback(() => {
    if (!session) {
      router.push('/sign-in');
      return;
    }

    router.navigate('/(tabs)/profile' as never);
  }, [session]);

  return (
    <View
      className="sticky top-0 z-50 border-b-[0.5px] border-white/[0.08] bg-[#090B0F] px-4"
      style={{ paddingTop: insets.top }}>
      <View className="mx-auto h-[52px] w-full max-w-screen-2xl flex-row items-center justify-between gap-4">
        <View className="flex-row items-center gap-4">
          <Pressable
            accessibilityLabel="Go to events"
            accessibilityRole="link"
            className="shrink-0 active:opacity-80"
            onPress={() => router.navigate('/(tabs)/events' as never)}>
            <RadiousWordmark />
          </Pressable>

          <View className="hidden flex-row items-center gap-1.5 md:flex">
            <Icon as={MapPinIcon} size={11} strokeWidth={2.5} className="text-[#ef4e2e]" />
            <Text className="font-body-medium text-xs text-white/55">{cityName}</Text>
          </View>
        </View>

        <View className="hidden h-full flex-1 items-center md:flex">
          <View className="h-full flex-row items-center gap-6">
            {TOP_NAV_ROUTES.map((name) => {
              const isFocused = activeTab === name;
              const label = TAB_CONFIG[name].label;
              const handlePress = () => router.navigate(`/(tabs)/${name}` as never);

              return (
                <Pressable
                  key={name}
                  accessibilityRole="tab"
                  accessibilityState={isFocused ? { selected: true } : {}}
                  className="relative h-full flex-row items-center gap-1.5 active:opacity-80"
                  onPress={handlePress}>
                  <Text
                    className={cn(
                      'text-sm',
                      isFocused
                        ? 'font-body-semibold text-white'
                        : 'font-body-medium text-white/45 hover:text-white/70'
                    )}>
                    {label}
                  </Text>
                  {name === 'activity' && pulseBadgeLabel ? (
                    <TabCountBadge label={pulseBadgeLabel} />
                  ) : null}
                  {isFocused ? (
                    <View className="absolute bottom-0 left-0 right-0 h-[2px] rounded-full bg-[#ef4e2e]" />
                  ) : null}
                </Pressable>
              );
            })}
          </View>
        </View>

        <View className="hidden shrink-0 flex-row items-center justify-end gap-3 md:flex">
          <WebSearchCombobox className="w-72" variant="nav" />
          <Pressable
            accessibilityRole="link"
            className="active:opacity-70"
            onPress={handleProfilePress}>
            <Text className="font-body-medium text-sm text-white/60 hover:text-white/85">
              {profileLabel}
            </Text>
          </Pressable>
          <GetAppButton />
        </View>

        <View className="flex-row items-center md:hidden">
          <Pressable
            accessibilityLabel="Open menu"
            accessibilityRole="button"
            className="size-9 items-center justify-center rounded-full bg-white/5 active:bg-white/10"
            onPress={() => undefined}>
            <Icon as={MenuIcon} size={20} strokeWidth={2.2} className="text-white/75" />
          </Pressable>
        </View>
      </View>
    </View>
  );
}

function GetAppButton() {
  const handlePress = React.useCallback(() => {
    Linking.openURL(APP_CONFIG.downloadURL).catch(() => undefined);
  }, []);

  return (
    <Pressable
      accessibilityLabel="Get the Radious app"
      accessibilityRole="button"
      className="flex-row items-center justify-center rounded-full bg-[#ef4e2e] px-4 py-2 active:bg-[#ff6f49]"
      onPress={handlePress}
    >
      <Text className="font-body-semibold text-sm leading-4 text-[#090B0F]">Get the app</Text>
    </Pressable>
  );
}

export function CreateEventButton({
  compact = false,
  onPress,
  visible = true,
}: {
  compact?: boolean;
  onPress?: () => void;
  visible?: boolean;
}) {
  const session = useAuth((state) => state.session);
  const canCreate = Boolean(session) && !isReadOnlyMarketAccess(session?.me.profile.market_access);

  if (!canCreate || !visible) {
    return null;
  }

  const handlePress = onPress ?? (() => router.push('/events/create'));

  if (compact) {
    return (
      <Pressable
        accessibilityLabel="Create event"
        accessibilityRole="button"
        className="w-full items-center justify-center"
        onPress={handlePress}>
        {({ pressed }) => (
          <View
            className={cn(
              'w-full items-center justify-center rounded-xl px-3 pb-1 pt-1.5',
              pressed && 'opacity-80'
            )}>
            <View
              className={cn(
                'size-8 items-center justify-center rounded-xl border',
                pressed ? 'border-primary bg-surface-accent' : 'border-primary-edge bg-surface'
              )}>
              <Icon
                as={PlusIcon}
                size={17}
                strokeWidth={2.5}
                className={pressed ? 'text-primary-pressed' : 'text-primary'}
              />
            </View>
          </View>
        )}
      </Pressable>
    );
  }

  return (
    <Pressable
      accessibilityLabel="Create event"
      accessibilityRole="button"
      className="flex-row items-center justify-center gap-2 rounded-full bg-[#ef4e2e] px-4 py-2 shadow-sm shadow-primary/20 active:bg-[#ff6f49]"
      onPress={handlePress}
    >
      <Icon as={PlusIcon} size={14} strokeWidth={2.6} className="text-[#090B0F]" />
      <Text className="font-body-semibold text-sm leading-4 text-[#090B0F]">Post event</Text>
    </Pressable>
  );
}

export function TabCountBadge({ label }: { label: string }) {
  return (
    <View className="h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1.5">
      <Text className="font-body-semibold text-xs leading-3 text-primary-foreground">
        {label}
      </Text>
    </View>
  );
}

function RadiousWordmark() {
  return (
    <Svg width={99} height={22} viewBox="0 0 310 69">
      <Path
        d="M0 66.78V22.5H17.64V66.78H0ZM17.64 42.84L9.45 35.55C11.73 30.75 14.37 27.24 17.37 25.02C20.43 22.74 24.24 21.6 28.8 21.6C30.84 21.6 32.58 21.84 34.02 22.32C35.52 22.8 36.72 23.49 37.62 24.39L27.72 38.43C27.3 37.95 26.67 37.56 25.83 37.26C25.05 36.9 24.12 36.72 23.04 36.72C21.24 36.72 19.89 37.26 18.99 38.34C18.09 39.36 17.64 40.86 17.64 42.84ZM52.8 67.68C48.66 67.68 45 66.69 41.82 64.71C38.64 62.73 36.15 60.03 34.35 56.61C32.55 53.13 31.65 49.14 31.65 44.64C31.65 40.14 32.55 36.15 34.35 32.67C36.15 29.19 38.64 26.49 41.82 24.57C45 22.59 48.66 21.6 52.8 21.6C55.38 21.6 57.75 22.05 59.91 22.95C62.13 23.79 63.96 24.99 65.4 26.55C66.9 28.11 67.86 29.91 68.28 31.95V57.33C67.86 59.37 66.9 61.17 65.4 62.73C63.96 64.29 62.13 65.52 59.91 66.42C57.75 67.26 55.38 67.68 52.8 67.68ZM56.76 52.2C58.98 52.2 60.75 51.51 62.07 50.13C63.45 48.69 64.14 46.86 64.14 44.64C64.14 43.14 63.84 41.85 63.24 40.77C62.64 39.63 61.77 38.73 60.63 38.07C59.55 37.41 58.29 37.08 56.85 37.08C55.41 37.08 54.12 37.41 52.98 38.07C51.9 38.73 51.03 39.63 50.37 40.77C49.71 41.91 49.38 43.2 49.38 44.64C49.38 46.08 49.68 47.37 50.28 48.51C50.94 49.65 51.84 50.55 52.98 51.21C54.12 51.87 55.38 52.2 56.76 52.2ZM63.6 66.78V54.9L66.03 44.1L63.6 33.39V22.5H80.79V66.78H63.6ZM103.95 67.68C99.57 67.68 95.73 66.69 92.43 64.71C89.19 62.73 86.64 60.03 84.78 56.61C82.98 53.13 82.08 49.14 82.08 44.64C82.08 40.14 82.98 36.15 84.78 32.67C86.64 29.19 89.19 26.49 92.43 24.57C95.73 22.59 99.57 21.6 103.95 21.6C106.59 21.6 109.08 22.05 111.42 22.95C113.76 23.79 115.74 24.99 117.36 26.55C119.04 28.11 120.09 29.91 120.51 31.95V56.43C120.09 58.47 119.04 60.36 117.36 62.1C115.74 63.78 113.76 65.13 111.42 66.15C109.08 67.17 106.59 67.68 103.95 67.68ZM107.19 52.2C108.69 52.2 109.98 51.9 111.06 51.3C112.14 50.64 112.98 49.74 113.58 48.6C114.24 47.46 114.57 46.14 114.57 44.64C114.57 43.14 114.27 41.85 113.67 40.77C113.07 39.63 112.2 38.73 111.06 38.07C109.98 37.41 108.72 37.08 107.28 37.08C105.84 37.08 104.55 37.41 103.41 38.07C102.33 38.73 101.46 39.63 100.8 40.77C100.14 41.91 99.81 43.2 99.81 44.64C99.81 46.08 100.11 47.37 100.71 48.51C101.37 49.65 102.27 50.55 103.41 51.21C104.55 51.87 105.81 52.2 107.19 52.2ZM131.22 66.78H114.03V54.9L116.46 44.1L113.58 33.39V0.9H131.22V66.78ZM135.12 66.78V22.5H152.76V66.78H135.12ZM143.94 18.36C141.36 18.36 139.2 17.49 137.46 15.75C135.78 13.95 134.94 11.76 134.94 9.18C134.94 6.6 135.78 4.44 137.46 2.7C139.2 0.9 141.36 0 143.94 0C146.58 0 148.74 0.9 150.42 2.7C152.1 4.44 152.94 6.6 152.94 9.18C152.94 11.76 152.1 13.95 150.42 15.75C148.74 17.49 146.58 18.36 143.94 18.36ZM178.96 67.86C174.1 67.86 169.75 66.87 165.91 64.89C162.13 62.85 159.13 60.06 156.91 56.52C154.75 52.98 153.67 48.99 153.67 44.55C153.67 40.11 154.75 36.15 156.91 32.67C159.07 29.19 162.04 26.46 165.82 24.48C169.66 22.44 174.01 21.42 178.87 21.42C183.73 21.42 188.05 22.44 191.83 24.48C195.61 26.46 198.58 29.19 200.74 32.67C202.96 36.15 204.07 40.11 204.07 44.55C204.07 48.99 202.99 52.98 200.83 56.52C198.67 60.06 195.7 62.85 191.92 64.89C188.14 66.87 183.82 67.86 178.96 67.86ZM178.87 52.38C180.37 52.38 181.66 52.08 182.74 51.48C183.88 50.82 184.75 49.92 185.35 48.78C185.95 47.58 186.25 46.2 186.25 44.64C186.25 43.08 185.92 41.73 185.26 40.59C184.66 39.39 183.82 38.49 182.74 37.89C181.66 37.23 180.37 36.9 178.87 36.9C177.43 36.9 176.14 37.23 175 37.89C173.92 38.55 173.05 39.48 172.39 40.68C171.79 41.82 171.49 43.17 171.49 44.73C171.49 46.23 171.79 47.58 172.39 48.78C173.05 49.92 173.92 50.82 175 51.48C176.14 52.08 177.43 52.38 178.87 52.38ZM226.11 67.86C221.79 67.86 217.95 67.02 214.59 65.34C211.29 63.66 208.68 61.32 206.76 58.32C204.84 55.32 203.88 51.9 203.88 48.06V22.5H221.52V48.24C221.52 49.14 221.7 49.95 222.06 50.67C222.48 51.39 223.02 51.96 223.68 52.38C224.4 52.74 225.21 52.92 226.11 52.92C227.37 52.92 228.42 52.5 229.26 51.66C230.16 50.76 230.61 49.62 230.61 48.24V22.5H248.25V48.06C248.25 51.96 247.29 55.41 245.37 58.41C243.51 61.35 240.9 63.66 237.54 65.34C234.24 67.02 230.43 67.86 226.11 67.86ZM268.64 68.13C265.88 68.13 263.12 67.77 260.36 67.05C257.6 66.33 255.02 65.34 252.62 64.08C250.28 62.82 248.3 61.38 246.68 59.76L255.95 50.22C257.39 51.66 259.1 52.8 261.08 53.64C263.12 54.48 265.25 54.9 267.47 54.9C268.49 54.9 269.27 54.78 269.81 54.54C270.41 54.24 270.71 53.85 270.71 53.37C270.71 52.53 270.2 51.9 269.18 51.48C268.22 51.06 266.96 50.67 265.4 50.31C263.9 49.95 262.28 49.5 260.54 48.96C258.86 48.36 257.24 47.58 255.68 46.62C254.12 45.6 252.86 44.22 251.9 42.48C250.94 40.68 250.46 38.4 250.46 35.64C250.46 32.82 251.24 30.33 252.8 28.17C254.42 25.95 256.67 24.21 259.55 22.95C262.49 21.63 265.88 20.97 269.72 20.97C273.62 20.97 277.4 21.63 281.06 22.95C284.72 24.27 287.63 26.25 289.79 28.89L280.43 38.43C278.93 36.75 277.28 35.64 275.48 35.1C273.68 34.5 272.18 34.2 270.98 34.2C269.84 34.2 269.03 34.38 268.55 34.74C268.13 35.04 267.92 35.43 267.92 35.91C267.92 36.57 268.4 37.11 269.36 37.53C270.32 37.95 271.55 38.34 273.05 38.7C274.61 39.06 276.23 39.54 277.91 40.14C279.59 40.74 281.18 41.58 282.68 42.66C284.24 43.74 285.5 45.18 286.46 46.98C287.42 48.72 287.9 51 287.9 53.82C287.9 58.2 286.16 61.68 282.68 64.26C279.2 66.84 274.52 68.13 268.64 68.13Z"
        fill="#FFFFFF"
      />
      <Path
        d="M299.9 67.86C297.08 67.86 294.74 66.9 292.88 64.98C291.02 63.06 290.09 60.72 290.09 57.96C290.09 55.14 291.02 52.77 292.88 50.85C294.74 48.93 297.08 47.97 299.9 47.97C302.72 47.97 305.03 48.93 306.83 50.85C308.69 52.77 309.62 55.14 309.62 57.96C309.62 60.72 308.69 63.06 306.83 64.98C305.03 66.9 302.72 67.86 299.9 67.86Z"
        fill="#ef4e2e"
      />
    </Svg>
  );
}
